import { useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { Download01Icon } from '@/components/icons/Download01Icon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';

import { $downloadStreamedFile } from '../files/utils/fileUtils';
import { useHouseholdDetailsContext } from '../household/contexts/householdDetails.context';
import { EducationListItemType } from './Education.constants';
import {
  useEducationMap,
  useEducationSlideOverrides,
} from './EducationSlideOverrides/EducationSlideOverrides.context';
import { usePrintEducationSlideMutation } from './graphql/PrintEducationSlide.generated';

export interface EducationExportButtonProps {
  activeSlide: EducationListItemType | null;
  customSlideIdentifier?: string | null;
}

export function EducationExportButton({
  activeSlide,
  customSlideIdentifier,
}: EducationExportButtonProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const trackUserEvent = useTrackUserEvent();
  const { showFeedback } = useFeedback();

  const [printEducationSlide, { error }] = usePrintEducationSlideMutation();
  const { householdId } = useHouseholdDetailsContext();
  const educationMap = useEducationMap();
  const { customSlidesMap } = useEducationSlideOverrides();

  // The slide identifier to use for the print route -- either a template slide or a custom slide
  const slideIdentifier = customSlideIdentifier ?? activeSlide;

  const { reportError } = useReportError('EducationExportButton', error, {
    activeSlide: slideIdentifier,
    householdId,
  });

  if (!slideIdentifier || !householdId) {
    return null;
  }

  const title = customSlideIdentifier
    ? (customSlidesMap[customSlideIdentifier]?.displayName ?? 'Custom page')
    : educationMap[activeSlide!].title;

  const handlePrint = async () => {
    try {
      if (!householdId) {
        throw new Error('No household ID found');
      }
      if (!slideIdentifier) {
        throw new Error('No active slide found');
      }

      setLoading(true);
      const path = getCompletePathFromRouteKey(
        ROUTE_KEYS.__PRINT_ONLY_EDUCATION_SLIDE,
        {
          householdId,
          activeSlide: slideIdentifier,
        }
      );

      // the lambda expects this whole path EXCEPT for the leading slash.
      const pathToPrint = path.slice(1, path.length);

      await printEducationSlide({
        variables: {
          householdId,
          pathToPrint,
        },
        onCompleted: async (data) => {
          const token = data.printV2?.token;
          if (token) {
            try {
              await $downloadStreamedFile(
                `/api/download/file?token=${token}`,
                `${title}.pdf`
              );
              trackUserEvent('presentation education slide exported', {
                activeSlide: slideIdentifier,
                householdId,
              });
              showFeedback('PDF generated successfully', {
                variant: 'success',
              });
            } catch (err) {
              showFeedback('Failed to download PDF. Please try again.');
            }
          } else {
            showFeedback('Failed to download PDF. Please try again.');
          }
          setLoading(false);
        },
        onError: () => {
          // we don't need to report the error here, because that's handled above by useReportError
          showFeedback('Failed to download PDF. Please try again.');
          setLoading(false);
        },
      });
    } catch (error) {
      reportError('Failed to print education slide', error as Error);
      showFeedback('Failed to download PDF. Please try again.');
    }
  };

  return (
    <Button
      variant="primary"
      size="sm"
      onClick={handlePrint}
      loading={loading}
      disabled={loading}
      startIcon={Download01Icon}
    >
      Export PDF
    </Button>
  );
}
